import { Link, useParams } from 'react-router-dom';
import { Calendar, User, Phone } from 'lucide-react';
import { blogPosts } from '../data/blog';
import { useState, useEffect } from 'react';
import { Navbar } from './Navbar';

export const BlogListPage = () => {
  // Set page title
  useEffect(() => {
    document.title = 'Blog & Artikel - Park Serpong | Tips, Panduan, dan Informasi Properti';
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <Navbar scrolled={true} currentPage="blog" />

      {/* Blog List - Add padding top for fixed navbar */}
      <div className="container mx-auto px-4 py-12 pt-24 md:pt-28">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 text-center" data-testid="blog-title">
            Artikel & Panduan
          </h1>
          <p className="text-xl text-gray-600 mb-12 text-center" data-testid="blog-subtitle">
            Tips, review, dan informasi lengkap seputar Park Serpong
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {blogPosts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:scale-105"
                data-testid={`blog-card-${post.id}`}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      {post.category}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-3 hover:text-blue-600 transition-colors">
                    {post.title}
                  </h2>
                  <p className="text-gray-600 mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <Calendar size={16} className="mr-1" />
                      {new Date(post.date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                    <div className="flex items-center">
                      <User size={16} className="mr-1" />
                      {post.author}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const BlogDetailPage = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);

  // Set page title dynamically based on article
  useEffect(() => {
    if (post) {
      document.title = `${post.title} | Blog Park Serpong`;
    }
  }, [post, slug]);

  // Scroll to top when article changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Artikel Tidak Ditemukan</h1>
          <Link to="/blog" className="text-blue-600 hover:text-blue-700">
            Kembali ke Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <Navbar scrolled={true} currentPage="blog" />

      {/* Article Hero - Add padding top for fixed navbar */}
      <div className="relative h-96 overflow-hidden mt-16 md:mt-20">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/70"></div>
        <div className="absolute inset-0 flex items-end">
          <div className="container mx-auto px-4 pb-12">
            <div className="max-w-4xl">
              <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4 inline-block">
                {post.category}
              </span>
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-4" data-testid="article-title">
                {post.title}
              </h1>
              <div className="flex items-center gap-6 text-white">
                <div className="flex items-center">
                  <Calendar size={18} className="mr-2" />
                  {new Date(post.date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
                <div className="flex items-center">
                  <User size={18} className="mr-2" />
                  {post.author}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <article className="bg-white rounded-2xl shadow-lg p-8 md:p-12 prose prose-lg max-w-none" data-testid="article-content">
            <div dangerouslySetInnerHTML={{ __html: post.content }} />
          </article>

          {/* CTA Section */}
          <div className="mt-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-center text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Tertarik dengan Park Serpong?
            </h3>
            <p className="text-lg mb-6">
              Hubungi kami untuk info lebih lanjut atau jadwalkan kunjungan
            </p>
            <a
              href="https://wa.me/628892273880?text=Halo!%20Saya%20ingin%20bertanya%20mengenai%20rumah%20di%20Park%20Serpong%20lebih%20lanjut.%20Terima%20Kasih"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors"
            >
              <Phone size={20} />
              Hubungi via WhatsApp
            </a>
          </div>

          {/* Related Articles */}
          <div className="mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Artikel Lainnya</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {blogPosts
                .filter(p => p.id !== post.id)
                .slice(0, 2)
                .map((relatedPost) => (
                  <Link
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.slug}`}
                    className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-bold text-gray-900 mb-2 hover:text-blue-600">
                        {relatedPost.title}
                      </h4>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
